import { Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, HStack, Spinner, FormControl, FormLabel, Select } from '@chakra-ui/react'
import React from 'react'
import { ErrorMessage } from '../components/ErrorMessage';
import { useOperation } from '../components/useOperation';
import { ICourse } from '@/shared/ICourse';
import { AutoResizeTextarea } from '../components/AutoResizeTextarea';
import { IUserInfo } from '@/shared/IUserInfo';
import { isSameEmail } from '@/shared/parse';

export const RemindProviderDialog = (props: {
  course: ICourse;
  onSendReminder: (course: ICourse, user: IUserInfo, message: string) => Promise<void>;
  onDismiss: () => void;
}) => {

  const { provider } = props.course;

  const [message, setMessage] = React.useState<string>('');

  // Combine course users with main provider contact
  const { availableUsers, defaultUserEmail } = React.useMemo(() => {
    const users: IUserInfo[] = [];
    // Add course users
    if (props.course.CourseProviderUsers?.length) {
      props.course.CourseProviderUsers.forEach(user => {
        users.push(user);
      });
    }

    const defaultCourseEmail = users.length > 0 ? users[0].email : '';

    if (provider?.ProviderUsers?.length) {
      provider.ProviderUsers.forEach(user => {
        if (!users.some(u => isSameEmail(u.email, user.email))) {
          users.push(user);
        }
      });
    }

    // Add main provider contact
    if (provider?.ProviderContact && provider?.ProviderEmail) {
      if (!users.some(u => isSameEmail(u.email, provider.ProviderEmail))) {
        users.push({
          id: `provider`,
          name: provider.ProviderContact,
          email: provider.ProviderEmail,
        });
      }
    }

    return {
      availableUsers: users.sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      defaultUserEmail: defaultCourseEmail || provider?.ProviderEmail || '',
    };
  }, [props.course.CourseProviderUsers, provider]);

  const [selectedUserEmail, setSelectedUserEmail] = React.useState<string>(defaultUserEmail);

  const [onSend, errorMessage, isProcessing] = useOperation(async () => {
    const user = availableUsers.find(u => u.email === selectedUserEmail);
    if (user) {
      await props.onSendReminder(props.course, user, message);
    }
  }, [selectedUserEmail, message]);

  const initialRef = React.useRef(null);

  const isDisabled = isProcessing || !selectedUserEmail;

  return (
    <Modal
      initialFocusRef={initialRef}
      finalFocusRef={initialRef}
      size='xl'
      isOpen
      onClose={props.onDismiss}
      preserveScrollBarGap
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Remind Provider</ModalHeader>
        <ModalCloseButton />        <ModalBody pb={6}>
          <ErrorMessage error={errorMessage} />

          <FormControl isRequired>
            <FormLabel>Send reminder to</FormLabel>
            <Select ref={initialRef} placeholder="Select user..." value={selectedUserEmail} onChange={(e) => setSelectedUserEmail(e.target.value)}>
              {availableUsers.map(user => (
                <option key={user.email} value={user.email}>
                  {user.name} ({user.email})
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Additional message to provider (optional)</FormLabel>
            <AutoResizeTextarea
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              minRows={3}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button variant='solid' isDisabled={isDisabled} mr={4} onClick={onSend}>
            <HStack spacing={2}>{isProcessing && <Spinner />}<Text>{isProcessing ? 'Sending...' : 'Send Reminder'}</Text></HStack>
          </Button>
          <Button variant='outline' onClick={props.onDismiss}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
